import { useCallback, useEffect, useState } from "react";

import PropTypes from "prop-types";
import { Controller, useForm } from "react-hook-form";

import { yupResolver } from "@hookform/resolvers/yup";
import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Stack,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid";

import { ProductSelect } from "@/features/products";
import { apiClient } from "@/features/shared";
import { CustomDialogTitle, CustomInputField } from "@/features/ui";
import {
  DEFAULT_VALUES,
  VALIDATION_SCHEMA,
} from "./product-variant-search-form-constants";

export const ProductVariantSearchFormDialog = ({
  defaultValues,
  onSubmit,
  onClose,
}) => {
  const { control, watch, handleSubmit, setValue, reset } = useForm({
    defaultValues: DEFAULT_VALUES,
    resolver: yupResolver(VALIDATION_SCHEMA),
  });

  const product = watch("product");
  const productVariant = watch("productVariant");
  const variantAttribute1Value = watch("variantAttribute1Value");
  const variantAttribute2Value = watch("variantAttribute2Value");
  const variantAttribute3Value = watch("variantAttribute3Value");
  const variantAttribute4Value = watch("variantAttribute4Value");

  const [loadingVariantAttributeValue, setLoadingVariantAttributeValue] =
    useState(false);
  const [productVariantAttributeValue, setProductVariantAttributeValue] =
    useState(null);
  const [loadingProductVariant, setLoadingProductVariant] = useState(false);

  const handleClose = () => {
    reset();
    onClose();
  };

  const fetchVariantAttributeValue = useCallback(async () => {
    setProductVariantAttributeValue(null);
    setLoadingVariantAttributeValue(true);
    try {
      const {
        data: { data },
      } = await apiClient.get(
        `products/${product.id}/product-variant-attribute-values`
      );
      setProductVariantAttributeValue(data);
    } finally {
      setLoadingVariantAttributeValue(false);
    }
  }, [product]);

  const fetchProductVariant = useCallback(async () => {
    setValue("productVariant", null);
    setLoadingProductVariant(true);
    try {
      const {
        data: { data },
      } = await apiClient.get(`products/${product.id}/product-variants`, {
        params: {
          variantAttribute1Value,
          variantAttribute2Value,
          variantAttribute3Value,
          variantAttribute4Value,
        },
      });
      if (data.length > 0) setValue("productVariant", data[0]);
    } finally {
      setLoadingProductVariant(false);
    }
  }, [
    product,
    setValue,
    variantAttribute1Value,
    variantAttribute2Value,
    variantAttribute3Value,
    variantAttribute4Value,
  ]);

  useEffect(() => {
    if (defaultValues) reset({ ...DEFAULT_VALUES, ...defaultValues });
  }, [defaultValues, reset]);

  useEffect(() => {
    if (product) {
      fetchVariantAttributeValue();
    }
  }, [fetchVariantAttributeValue, product]);

  const handleProductChange = (newValue) => {
    setProductVariantAttributeValue(null);
    reset({
      ...DEFAULT_VALUES,
      product: newValue,
      variantAttribute1Value: null,
      variantAttribute2Value: null,
      variantAttribute3Value: null,
      variantAttribute4Value: null,
      productVariant: null,
    });
  };

  useEffect(() => {
    if (productVariantAttributeValue) {
      if (
        productVariantAttributeValue.variantAttribute1 &&
        !variantAttribute1Value
      ) {
        return;
      }
      if (
        productVariantAttributeValue.variantAttribute2 &&
        !variantAttribute2Value
      ) {
        return;
      }
      if (
        productVariantAttributeValue.variantAttribute3 &&
        !variantAttribute3Value
      ) {
        return;
      }
      if (
        productVariantAttributeValue.variantAttribute4 &&
        !variantAttribute4Value
      ) {
        return;
      }

      fetchProductVariant();
    }
  }, [
    fetchProductVariant,
    productVariantAttributeValue,
    variantAttribute1Value,
    variantAttribute2Value,
    variantAttribute3Value,
    variantAttribute4Value,
  ]);

  const onFormSubmit = (data) => {
    onSubmit(data);
    handleClose();
  };

  const renderVariantAttributeField = (index) => {
    const attributeKey = `variantAttribute${index + 1}`;
    const attributeValueKey = `variantAttribute${index + 1}Value`;

    if (!productVariantAttributeValue?.[attributeKey]) return null;

    return (
      <Grid key={attributeKey} size={{ xs: 6 }}>
        <Controller
          control={control}
          name={attributeValueKey}
          render={({ field }) => (
            <Autocomplete
              fullWidth
              value={field.value}
              onChange={(_, newValue) => setValue(attributeValueKey, newValue)}
              disableClearable
              options={
                productVariantAttributeValue[`${attributeKey}Values`] || []
              }
              renderInput={(params) => (
                <CustomInputField
                  {...params}
                  required
                  label={productVariantAttributeValue[attributeKey]?.name}
                  InputProps={{ ...params.slotProps.input, placeholder: "Pilih" }}
                />
              )}
            />
          )}
        />
      </Grid>
    );
  };

  return (
    <Dialog
      open
      onClose={handleClose}
      fullWidth
      component="form"
      onSubmit={handleSubmit(onFormSubmit)}
    >
      <CustomDialogTitle
        variant="h5"
        title="Cari Produk Varian"
        onClose={handleClose}
      />
      <DialogContent>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12 }}>
            <Controller
              control={control}
              name="product"
              render={({ field, fieldState }) => (
                <ProductSelect
                  value={field.value}
                  onChange={handleProductChange}
                  TextFieldProps={{
                    required: true,
                    error: fieldState.invalid,
                    helperText: fieldState.error?.message,
                  }}
                />
              )}
            />
          </Grid>
          {loadingVariantAttributeValue && (
            <Grid size={{ xs: 12 }}>
              <Typography
                variant="body2"
                sx={{
                  fontStyle: "italic",
                  color: "gray",
                }}
              >
                Memuat atribut varian...
              </Typography>
            </Grid>
          )}
          {[...Array(4)].map((_, index) => renderVariantAttributeField(index))}
          {loadingProductVariant && (
            <Grid size={{ xs: 12 }}>
              <Typography
                variant="body2"
                sx={{
                  fontStyle: "italic",
                  color: "gray",
                }}
              >
                Memuat informasi produk varian...
              </Typography>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      {productVariant && (
        <DialogActions>
          <Box
            sx={{
              mr: 1,
            }}
          >
            <Typography
              align="right"
              sx={{
                fontSize: 10,
                color: "grey",
              }}
            >
              SKU
            </Typography>
            <Stack direction="row" sx={{
              alignItems: "baseline"
            }}>
              {watch("discount") > 0 && (
                <Typography
                  sx={{
                    fontSize: 11,
                    fontWeight: "medium",
                    textDecoration: "line-through",
                    mr: 0.5,
                    color: "grey",
                  }}
                >
                  {productVariant.sku}
                </Typography>
              )}
              <Typography
                sx={{
                  fontSize: 14,
                  fontWeight: "medium",
                }}
              >
                {productVariant.sku}
              </Typography>
            </Stack>
          </Box>
          <Button type="submit" variant="contained">
            Pilih
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

ProductVariantSearchFormDialog.propTypes = {
  defaultValues: PropTypes.object,
  onSubmit: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};
